import { ILocationService, IQService, IWindowService, jwt } from "angular";
import { StateService } from "@uirouter/angularjs";
import auth0, { IAuthResult, IIdTokenPayload } from "@gtmhub/auth0";
import { IAppConfig, isLocalhost } from "@gtmhub/env";
import { InviteUserToAccountService } from "../services/inviteUserToAccountService";
import { AuthCookie, AuthSpaJsCookie, getAuth0, setAuth0UserId } from "@gtmhub/auth";
import { LoggingService, stringifyMessage } from "@gtmhub/core/logging";
import { Auth0ClientOptions } from "@auth0/auth0-spa-js";

const loginsCountPropName = "https://gtmhub.com/loginsCount";

export class ResolveTokenCtrl {
  private loggingService: LoggingService;

  public static $inject = ["$location", "$window", "$state", "$q", "appConfig", "jwtHelper", "InviteUserToAccountService"];

  constructor(
    private $location: ILocationService,
    private $window: IWindowService,
    private $state: StateService,
    private $q: IQService,
    private appConfig: IAppConfig,
    private jwtHelper: jwt.IJwtHelper,
    private inviteUserToAccountService: InviteUserToAccountService
  ) {
    this.loggingService = new LoggingService(appConfig.logging, "spa");
    this.resolveToken();
  }

  private navigateToNextPhase(appState) {
    if (appState && appState.returnPath && appState.returnPath.startsWith("/")) {
      // if we are inside the case where we have a return path and we are going to the root of the website - add the window.location.origin
      this.$window.location.href = appState.returnPath;
    } else {
      this.goToState("accountBootstrap.resolveAccount");
    }
  }

  private resolveTokenPkce = (appState) => {
    let url = window.location.href;
    url = url.replace(/%20/g, "%2b");
    const auth0 = getAuth0();
    const clientId = this.$location.search().clientId;

    AuthSpaJsCookie.remove(clientId);
    auth0
      .handleRedirectCallback(url)
      .then(() => {
        this.$q.all([auth0.getIdTokenClaims(), auth0.getTokenSilently()]).then((result) => {
          const [idToken, accessToken] = result;
          const auth0UserId = idToken.sub;
          localStorage.setItem("auth0UserId", JSON.stringify(auth0UserId));
          localStorage.setItem("accessToken", accessToken);
          localStorage.setItem("token", JSON.stringify(idToken.__raw));
          document.cookie = `auth0UserId=${encodeURIComponent(auth0UserId)}`;

          localStorage.removeItem("accountId");
          localStorage.removeItem("userId");
          // setting loginsCount to localStorage as it is needed to decide whether to show delighted survey or not
          this.setLoginsCountToLocalStorage(idToken);

          const invitationKey = new URLSearchParams(url).get("invitationKey");

          if (invitationKey) {
            this.inviteUserToAccountService.addUserToAccount(decodeURIComponent(invitationKey));
          } else {
            this.navigateToNextPhase(appState);
          }
        });
      })
      .catch((e) => {
        console.error(e);
        this.$q.when(auth0.logout()).then(() => this.handleError({ error: e }));
      });
  };

  private resolveToken = () => {
    // URL is /resolve-token/?access_token=...
    const url = this.$location.url();
    // get query string as object
    const query = this.$location.search();
    // convert query string to hash for auth0.WebAuth.parseHash
    const hash = "#" + url.substring(url.indexOf("?") + 1);

    // if coming from ms-teams - the cookie should contain "SameSite=None" due to chrome security concern
    const isMsTeamsState = this.$window.location.href.includes("msteams");

    // ensure we have an auth cookie present
    const { webappEndpoint, secure } = this.appConfig.endpoints;
    const authCookie = new AuthCookie(webappEndpoint, secure, isMsTeamsState);
    const authCookieValue = authCookie.getAndRemove();
    if (!authCookieValue) {
      return this.handleError({ error: "Cannot find state cookie to verify authentication" });
    }

    const { state, nonce, appState } = authCookieValue;
    const isImplicitWorkflow = !url.includes("code");
    // this handles the token for the implicit flow
    if (isImplicitWorkflow) {
      this.resolveTokenImplicitFlow(query, hash, state, nonce, appState);
    } else {
      const host = this.$location.host();
      const auth0ClientOptions: Auth0ClientOptions = {
        domain: query.domain,
        client_id: query.clientId,
        audience: query.audience,
        redirect_uri: `${this.$location.protocol()}://${host}/login/callback`,
        useRefreshTokens: true,
        cacheLocation: "localstorage",
        cookieDomain: isLocalhost(host) ? host : "gtmhub.com",
        useCookiesForTransactions: true,
      };
      localStorage.setItem("auth0", JSON.stringify(auth0ClientOptions));
      this.resolveTokenPkce(appState);
    }
  };

  private resolveTokenImplicitFlow(query, hash: string, state: string, nonce: string, appState: Record<string, string>) {
    const webAuth = new auth0.WebAuth({
      clientID: query.clientId,
      domain: query.domain,
      audience: query.audience,
      scope: "openid email",
      responseType: "token id_token",
    });
    webAuth.parseHash(
      {
        hash,
        state,
        nonce,
        __enableIdPInitiatedLogin: true,
      },
      (err: Record<string, unknown>, authResult: IAuthResult) => {
        if (err) {
          const errString = JSON.stringify(err);
          console.warn("error encountered while parsing authentication token: " + errString);
          return this.handleError(err);
        }
        if (!authResult.idToken) {
          return this.handleError({ error: "The supplied authentication token is invalid" });
        }
        if (!authResult.idTokenPayload || !authResult.idTokenPayload.sub) {
          return this.handleError({ error: "The received identity token is invalid" });
        }

        // Save token and profile to be used by the SPA app
        localStorage.setItem("token", JSON.stringify(authResult.idToken));
        setAuth0UserId(authResult.idTokenPayload.sub);

        try {
          // if audience is not set, accessToken will not be valid jwt and the decodeToken method will throw an error
          this.jwtHelper.decodeToken(authResult.accessToken);
          localStorage.setItem("accessToken", authResult.accessToken);
        } catch (error) {
          // if accesssToken format is not valid - log the error so we can track if some web app entries are skipped or with wrong auth configuration
          if (this.appConfig.logging.enabled) {
            const errorMessage = `provided accessToken for ${authResult.idTokenPayload.sub} is not a valid jwt. details: ${stringifyMessage(error)}`;
            this.loggingService.logError(new Error(errorMessage));
          }
        }
        localStorage.removeItem("accountId");
        localStorage.removeItem("userId");

        // setting loginsCount to localStorage as it is needed to decide whether to show delighted survey or not
        this.setLoginsCountToLocalStorage(authResult.idTokenPayload);

        const invitationKey = hash.split("invitationKey=")[1];

        if (invitationKey) {
          this.inviteUserToAccountService.addUserToAccount(decodeURIComponent(invitationKey));
        } else {
          this.navigateToNextPhase(appState);
        }
      }
    );
  }

  private handleError = (error: Record<string, unknown>) => {
    const errorMessage = stringifyMessage(error);
    console.error(errorMessage);
    this.loggingService.logError(new Error(errorMessage));

    localStorage.setItem("auth_error", errorMessage);
    this.goToState("error");
  };

  private goToState = (state: string): void => {
    // replace the location to prevent saving this URL in browser history
    this.$state.go(state, {}, { location: "replace", inherit: false });
  };

  private setLoginsCountToLocalStorage = (idTokenPayload: IIdTokenPayload): void => {
    // authResult.idTokenPayload is checked if existing above so skipping it
    if (idTokenPayload[loginsCountPropName]) {
      localStorage.setItem("loginsCount", idTokenPayload[loginsCountPropName].toString());
    }
  };
}
